import React, { useState } from 'react'
import { Box, Collapse, Divider, ListItemButton, ListItemIcon, ListItemText, Stack, Typography, useTheme } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import Icon from '@mdi/react'
import { mdiInformation, mdiMinusBox, mdiPlusBox } from '@mdi/js'
import { useBreakpoints } from '../../../../components/breakpoints/BreakpointsProvider'
import { simulateurConsts, SIMULATOR_FIELD } from '../services/simulatorConstants'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts'
import ButtonDefault from '../../../../components/ButtonDefault'
import { SECONDARY_PATH } from '../../../../constant/path'
import { useHistory } from 'react-router-dom'
import NetworkPartnerCard from '../../dashboard/components/NetworkPartnerCard'
import SanteClairBlock from '../../serviceSante/components/SanteClairBlock'
import { ACCES_RESEAU_GA_ACTION, LIEN_ITELIS, SERVICES_BIEN_ETRE_GA_CATEGORY } from '../../../../utils/servicesUtils'
import ReactGA from 'react-ga'
import ButtonDashboard from '../../../../components/ButtonDashboard'
import TeleconsultationCard from '../../serviceSante/components/TeleconsultationCard'
import DashboardCard from '../../../../components/DashboardCard'

type SimulationChartProps = {
	depense: string
	hypotheses: string
	rembSs: number
	resteACharge: number
	rembGerep: number
	sousTypeActe: string
	brss: number
	txss: number
	franchise: number
	garantie: string,
	has100Sante: boolean,
	resetFormAction: any,
	user: any,
	service: any,
	sante100Content?: any
}

const MEDIA_GUIDE_SANTE_SHORTCUT = 'guide_gestion_sante'

const SimulationChart: React.FC<SimulationChartProps> = (
	{
		depense,
		rembSs,
		resteACharge,
		rembGerep,
		brss,
		txss,
		franchise,
		garantie,
		has100Sante,
		resetFormAction,
		user,
		sante100Content,
		service
	}
) => {
	const intl = useIntl()
	const theme = useTheme()
	const { isMobile, isTablet, isTabletPortrait } = useBreakpoints()
	const history = useHistory()

	const [openInfo, setOpenInfo] = useState<boolean>(true)

	const getPercentage = (value: number) => {
		const percent = (value / parseFloat(depense)) * 100
		return parseFloat(percent.toFixed(2))
	}

	const isMobileOrTablet = isMobile || isTablet || isTabletPortrait

	const getOptions = () => {
		return {
			chart: {
				height: isMobile ? 300 : 400,
				type: 'pie',
				backgroundColor: null,
				plotBackgroundColor: null,
				plotBorderWidth: null,
				plotShadow: false
			},
			title: {
				text: ''
			},
			tooltip: {
				enabled: false
			},
			plotOptions: {
				pie: {
					allowPointSelect: false,
					cursor: 'pointer',
					dataLabels: {
						enabled: !isMobile,
						style: {
							fontSize: '15px',
							fontWeight: 'normal',
							fontFamily: 'Montserrat'
						}
					}
				}
			},
			series: [{
				innerSize: '30%',
				name: 'Brands',
				colorByPoint: true,
				dataLabels: {
					align: 'center',
					color: '#000'
				},
				borderWidth: 10,
				startAngle: -70,
				data: [{
					name: intl.formatMessage({ id: 'simulation.result.label.socialSecurity' }, { amount: intl.formatNumber(rembSs, { minimumFractionDigits: 2 }) }).replace('.', ','),
					y: rembSs,
					color: simulateurConsts.COLOR_REMB_SS
				}, {
					name: intl.formatMessage({ id: 'simulation.result.label.gerep' }, { amount: intl.formatNumber(rembGerep, { minimumFractionDigits: 2 }) }).replace('.', ','),
					y: rembGerep,
					color: simulateurConsts.COLOR_REMB_GEREP
				}, {
					name: intl.formatMessage({ id: 'simulation.result.label.rest' }, { amount: intl.formatNumber(resteACharge, { minimumFractionDigits: 2 }) }).replace('.', ','),
					y: resteACharge,
					color: simulateurConsts.COLOR_RAC
				}].filter(serie => serie.y > 0)
			}],
			credits: {
				enabled: false
			},
			exporting: {
				enabled: false
			}
		}
	}

	const CHART_DATA = [
		{
			x: ' ',
			label: (resteACharge !== 0 && !isMobile && intl.formatMessage({ id: 'simulation.result.label.rest' }, { amount: resteACharge })) || undefined,
			legend: intl.formatMessage({ id: 'simulation.result.label.rest' }, { amount: resteACharge.toFixed(2).replace('.', ',') }).toUpperCase(),
			y: getPercentage(resteACharge),
			fill: simulateurConsts.COLOR_RAC,
			total: resteACharge
		},
		{
			x: ' ',
			label: (rembSs !== 0 && !isMobile && intl.formatMessage({ id: 'simulation.result.label.socialSecurity' }, { amount: rembSs })) || undefined,
			legend: intl.formatMessage({ id: 'simulation.result.label.socialSecurity' }, { amount: rembSs.toFixed(2).replace('.', ',') }),
			y: getPercentage(rembSs),
			fill: simulateurConsts.COLOR_REMB_SS,
			total: rembSs
		},
		{
			x: ' ',
			label: (rembGerep !== 0 && !isMobile && intl.formatMessage({ id: 'simulation.result.label.gerep' }, { amount: rembGerep })) || undefined,
			legend: intl.formatMessage({ id: 'simulation.result.label.gerep' }, { amount: rembGerep.toFixed(2).replace('.', ',') }),
			y: getPercentage(rembGerep),
			fill: simulateurConsts.COLOR_REMB_GEREP,
			total: rembGerep
		}
	]

	return (
		<Stack direction="column" spacing="24px" justifyContent="center">
			<Stack direction={isMobileOrTablet ? 'column' : 'row'} spacing="12px" justifyContent="center">
				<Stack direction="column" width="100%" alignItems="center">
					<HighchartsReact highcharts={Highcharts} options={getOptions()} />
				</Stack>
				<Stack direction="column" width="100%" alignItems="center" justifyContent="center">
					<Stack direction="column" spacing="16px">
						{CHART_DATA.map((data, index) => (
							<Stack key={index} direction="row" alignItems="center" spacing="12px">
								<Box sx={{ height: '16px', width: '16px', borderRadius: '16px', backgroundColor: data.fill }} />
								<Typography variant="h3">
									{data.legend}
								</Typography>
							</Stack>
						))}
					</Stack>
				</Stack>
			</Stack>
			<Stack direction={isMobile ? 'column' : 'row'} spacing="16px" display="flex" justifyContent="center">
				<ButtonDefault variant="outlined" type="submit" onClick={() => history.push(SECONDARY_PATH.SERVICES_SEND_QUOTE)}>
					<FormattedMessage id="simulation.button.redirectDevis" />
				</ButtonDefault>
				<ButtonDefault variant="contained" type="submit" onClick={() => {
					resetFormAction()
				}}>
					<FormattedMessage id="simulation.button.return" />
				</ButtonDefault>
			</Stack>
			<Divider />
			<Typography textAlign="left" variant="h2">
				{intl.formatMessage({ id: 'simulation.result.titleInfo' }).toUpperCase()}
			</Typography>
			{ (has100Sante && sante100Content) &&
				<DashboardCard height='auto' title={intl.formatMessage({ id: 'simulation.result.subtitle100Sante' })} cardContentPadding={!isMobile ? '16px 24px 24px 24px' : undefined}>
					<Stack direction="column"spacing="24px" width="100%" alignItems="center">
						<Stack direction="column" width="100%" alignItems="center">
							<Typography variant="body2">
								<div dangerouslySetInnerHTML={{ __html: (sante100Content && sante100Content.fields && sante100Content.fields.body) }} />
							</Typography>
						</Stack>
						<Stack direction="row"  spacing="12px" width="100%" alignItems="center">
							<Icon path={mdiInformation} style={{width: "40px", height: "30px"}} color="#004687" />
							<Typography textAlign="left" variant="h2">
								{intl.formatMessage({ id: 'simulation.result.info100sante' })}
								<a href={`/blobs/medias/s/${MEDIA_GUIDE_SANTE_SHORTCUT}/Flyer_100Sante_Gerep.pdf`}
								   style={{
									   textDecoration: 'none',
									   color: '#E96E11',
								   }}
								   target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: 'simulation.result.info100santeLink' })}</a>
							</Typography>
							<Typography textAlign="left" variant="h2">
							</Typography>
						</Stack>
					</Stack>
				</DashboardCard>
			}
			{user.profil.santeclair &&
				<SanteClairBlock simulateurTitre={intl.formatMessage({ id: 'simulation.result.subtitleInfo' })} simulateur={true} service={service} />
			}
			{user.profil.itelis &&
				<TeleconsultationCard
					service={service}
					simulateurTitre={intl.formatMessage({ id: 'simulation.result.subtitleInfo' })}
					simulateur={true}
					lienWebCustom={
						<a
							href={LIEN_ITELIS}
							target="_blank"
							rel="noopener noreferrer"
							style={{ textDecoration: 'none', display: 'flex', justifyContent: 'center' }}
							onClick={() => {
								ReactGA.event({
									category: SERVICES_BIEN_ETRE_GA_CATEGORY,
									action: ACCES_RESEAU_GA_ACTION,
									label: 'ITELIS'
								})
							}}
						>
							<ButtonDashboard>
								{service?.libelleLienWeb || 'Accédez au réseau'}
							</ButtonDashboard>
						</a>
					}
				/>
			}
			<ListItemButton style={{ paddingLeft: 0, paddingBottom: 0 }} onClick={() => setOpenInfo(prevState => !prevState)}>
				<ListItemIcon>
					<Icon path={openInfo ? mdiMinusBox : mdiPlusBox} size="24px" color={theme.palette.primary.main} />
				</ListItemIcon>
				<ListItemText primary={intl.formatMessage({ id: 'simulation.result.calcul' })} />
			</ListItemButton>
			<Collapse in={openInfo}>
				<Typography variant="body2">
					<FormattedMessage id="simulation.result.hypothese" values={{
						br: <br />,
						brss: brss,
						txss: txss * 100,
						franchise: franchise,
						garantie: garantie
					}} />
				</Typography>
			</Collapse>
		</Stack>
	)
}

export default SimulationChart
